import React from 'react';
import { useAutoMatch } from '../context/AutoMatchContext';
import { 
  Loader2, 
  X, 
  Minimize2, 
  Maximize2,
  StopCircle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const FloatingAutoMatchProgress = () => {
  const { 
    progress, 
    isRunning, 
    isMinimized, 
    isHidden,
    setIsMinimized, 
    setIsHidden,
    stopAutoMatch 
  } = useAutoMatch();

  // Don't render if no progress or hidden
  if (!progress || isHidden) {
    // Show a small "show" button if hidden and running
    if (isRunning && isHidden) {
      return (
        <button
          onClick={() => setIsHidden(false)}
          className="fixed bottom-4 right-4 z-50 p-3 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-all"
          title="Eşleştirme durumunu göster"
        >
          <Eye size={20} />
        </button>
      );
    }
    return null;
  }

  // If not running and progress exists, show completion message briefly
  if (!isRunning && progress.processed > 0) {
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-80 animate-fade-in">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle size={20} />
            <span className="font-medium">Eşleştirme Tamamlandı</span>
          </div>
          <button
            onClick={() => setIsHidden(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>
        <div className="text-sm text-gray-600">
          <span className="text-green-600 font-medium">{progress.matched}</span> eşleşti, 
          <span className="text-orange-600 font-medium ml-1">{progress.not_matched}</span> eşleşmedi
        </div>
      </div>
    );
  }

  // Minimized view
  if (isMinimized) {
    const percentage = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;
    
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-full shadow-2xl px-4 py-2 flex items-center gap-3 cursor-pointer hover:scale-105 transition-all"
           onClick={() => setIsMinimized(false)}>
        <Loader2 className="animate-spin" size={18} />
        <span className="font-medium">{percentage}%</span>
        <span className="text-xs opacity-80">({progress.processed}/{progress.total})</span>
        <Maximize2 size={16} className="opacity-70" />
      </div>
    );
  }

  // Full view
  const percentage = progress.total > 0 ? (progress.processed / progress.total) * 100 : 0;
  
  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-2xl border border-gray-200 w-96 overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Loader2 className="animate-spin" size={18} />
          <span className="font-medium text-sm">Otomatik Eşleştirme</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white/20 rounded"
            title="Küçült"
          >
            <Minimize2 size={16} />
          </button>
          <button
            onClick={() => setIsHidden(true)}
            className="p-1 hover:bg-white/20 rounded"
            title="Gizle"
          >
            <EyeOff size={16} />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 pt-3">
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
          <div 
            className="bg-gradient-to-r from-green-500 to-blue-500 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mb-3">
          <span>{progress.processed} / {progress.total}</span>
          <span>{Math.round(percentage)}%</span>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 pb-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-green-50 rounded p-2">
          <div className="text-green-600 font-bold">{progress.matched}</div>
          <div className="text-gray-500">Eşleşti</div>
        </div>
        <div className="bg-orange-50 rounded p-2">
          <div className="text-orange-600 font-bold">{progress.not_matched}</div>
          <div className="text-gray-500">Eşleşmedi</div>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <div className="text-gray-600 font-bold">
            {progress.estimated_remaining != null 
              ? `${Math.ceil(progress.estimated_remaining / 60)}dk`
              : '...'
            }
          </div>
          <div className="text-gray-500">Kalan</div>
        </div>
      </div>

      {/* Stop Button */}
      <div className="px-4 pb-3">
        <button
          onClick={stopAutoMatch}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 text-sm transition-colors"
        >
          <StopCircle size={16} />
          Durdur
        </button>
      </div>
    </div>
  );
};

export default FloatingAutoMatchProgress;
